// Endless-mode puzzle queue. Maintains a per-type buffer of pre-generated
// puzzles so the user never waits for /puzzle between attempts. Threat
// puzzles take ~4-7s to generate at higher n, so the queue prioritises
// keeping the threat slot stocked while the fast types (win/passthrough/
// denial) are refilled near-instantly on demand.
//
// Queue target (per-type slots, default):
//   { win: 1, passthrough: 1, denial: 1, threat: 3 }
//
// Serving discipline:
//   - The first `WARMUP_PUZZLES` (default 3) only serve fast types so the
//     user never waits at puzzle 1 while a threat is being generated.
//   - After warmup, a threat is served whenever its slot has anything;
//     otherwise we fall back to a fast type. This naturally caps threat
//     rate at the refill rate (a slow type can only appear as fast as it
//     is refilled).
//
// All puzzle fetches share the per-type config the user picked on the
// endless-config screen (puzzle_type, num_jumps range, noise, player).
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPuzzleFromCatalog } from '../puzzleCatalog';

const DEFAULT_TARGET = { win: 1, passthrough: 1, denial: 1, threat: 3 };
const FAST_TYPES = ['win', 'passthrough', 'denial'];
const WARMUP_PUZZLES = 3;
const MAX_CONCURRENT_FETCHES = 2;

function pickN(typeCfg) {
  // typeCfg.minN / typeCfg.maxN inclusive; uniform random.
  const lo = typeCfg.minN;
  const hi = typeCfg.maxN ?? lo;
  if (hi <= lo) return lo;
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function pickPlayer(typeCfg) {
  if (typeCfg.player === 1 || typeCfg.player === 2) return typeCfg.player;
  return 1 + Math.floor(Math.random() * 2); // mix it up if not pinned
}

async function fetchOne(apiBase, type, typeCfg, rows, cols) {
  // Resolve from the static catalog instead of /puzzle?... so endless mode
  // works on the hosted site where there is no JAX backend. The catalog's
  // entries were generated with the type's default noise/max_jump_len, so
  // those typeCfg knobs only affect future regenerations of the catalog.
  return fetchPuzzleFromCatalog({
    puzzle_type: type,
    num_jumps: pickN(typeCfg),
    player: pickPlayer(typeCfg),
  });
}

export function usePuzzleQueue({
  enabled,
  apiBase,
  rows,
  cols,
  perTypeConfig,                 // { win: {minN,maxN,noise,maxJumpLen,player}, threat: {...}, ... }
  enabledTypes,                  // ['win','threat','passthrough','denial']  user-selected pool
  target = DEFAULT_TARGET,
}) {
  const [queue, setQueue] = useState({ win: [], passthrough: [], denial: [], threat: [] });
  const queueRef = useRef(queue);
  useEffect(() => { queueRef.current = queue; }, [queue]);

  const inFlightRef = useRef({ win: 0, passthrough: 0, denial: 0, threat: 0 });
  const cancelledRef = useRef(false);

  // Topup loop: pick the next slot to fill, fetch one, push into the slot
  // when it arrives. Caps total concurrent fetches.
  //
  // Priority order: fast types first (they generate in <1s, so getting one
  // of each into stock is the fastest path to "ready to serve puzzle 1").
  // Threat is only fetched once every fast type has its target met. This
  // avoids the warmup race where a naive biggest-gap heuristic would
  // grab two threat fetches up front and starve fast types for ~5 seconds.
  const tryFillSlot = useCallback(async () => {
    if (cancelledRef.current || !enabled) return;
    const q = queueRef.current;
    const inFlight = inFlightRef.current;
    const totalInFlight = Object.values(inFlight).reduce((a, b) => a + b, 0);
    if (totalInFlight >= MAX_CONCURRENT_FETCHES) return;

    const needs = (t) => (target[t] ?? 0) - (q[t]?.length ?? 0) - (inFlight[t] ?? 0);

    // 1. Fast types: any below target, in declared order.
    let bestType = null;
    for (const t of FAST_TYPES) {
      if (!enabledTypes.includes(t)) continue;
      if (needs(t) > 0) { bestType = t; break; }
    }
    // 2. Slow types (threat etc.): fill any remaining slots.
    if (!bestType) {
      for (const t of enabledTypes) {
        if (FAST_TYPES.includes(t)) continue;
        if (needs(t) > 0) { bestType = t; break; }
      }
    }
    if (!bestType) return;

    inFlightRef.current = { ...inFlight, [bestType]: (inFlight[bestType] ?? 0) + 1 };
    try {
      const data = await fetchOne(apiBase, bestType, perTypeConfig[bestType] || {}, rows, cols);
      if (cancelledRef.current) return;
      setQueue(prev => ({ ...prev, [bestType]: [...(prev[bestType] || []), data] }));
    } catch (err) {
      console.warn('[usePuzzleQueue] fetch failed', bestType, err);
    } finally {
      const cur = inFlightRef.current;
      inFlightRef.current = { ...cur, [bestType]: Math.max(0, (cur[bestType] ?? 1) - 1) };
      // Kick another fill if there's more to do.
      setTimeout(() => tryFillSlot(), 0);
    }
  }, [enabled, apiBase, rows, cols, perTypeConfig, enabledTypes, target]);

  // Keep MAX_CONCURRENT_FETCHES requests in flight whenever the queue is hungry.
  useEffect(() => {
    if (!enabled) return;
    for (let i = 0; i < MAX_CONCURRENT_FETCHES; i++) tryFillSlot();
  }, [enabled, tryFillSlot]);

  // Reset when disabled (e.g. leaving endless mode).
  useEffect(() => {
    cancelledRef.current = !enabled;
    if (!enabled) {
      setQueue({ win: [], passthrough: [], denial: [], threat: [] });
      inFlightRef.current = { win: 0, passthrough: 0, denial: 0, threat: 0 };
    }
  }, [enabled]);

  // Serve the next puzzle for the given served-count (0-based index of the
  // upcoming puzzle in this endless run). Returns the served puzzle data
  // or null if the queue can't satisfy the request right now (caller can
  // fall back to a synchronous fetch or wait).
  const servedCountRef = useRef(0);
  const popNext = useCallback(() => {
    const idx = servedCountRef.current;
    servedCountRef.current = idx + 1;
    const q = queueRef.current;

    const inWarmup = idx < WARMUP_PUZZLES;

    // Build the rotation order. During warmup only fast types are eligible
    // so the user never waits for a threat to generate on puzzle 1-3. After
    // warmup the rotation includes every enabled type in declared order:
    // over N puzzles each enabled type gets about 1/N of the appearances,
    // instead of threat dominating once its queue is stocked.
    const pool = inWarmup
      ? FAST_TYPES.filter(t => enabledTypes.includes(t))
      : enabledTypes;
    if (pool.length === 0) {
      servedCountRef.current = idx;
      return null;
    }

    // Walk the rotation starting at this puzzle's slot in the cycle. First
    // pass: the type whose turn it is. Fallbacks: any other in-pool type
    // that happens to have stock.
    let chosenType = null;
    let chosenPuzzle = null;
    for (let i = 0; i < pool.length; i++) {
      const t = pool[(idx + i) % pool.length];
      if ((q[t]?.length ?? 0) > 0) { chosenType = t; chosenPuzzle = q[t][0]; break; }
    }

    if (!chosenPuzzle) {
      // Nothing ready yet. Roll back so the next call retries this slot
      // (without wasting the rotation cursor on an empty serve).
      servedCountRef.current = idx;
      return null;
    }

    setQueue(prev => ({ ...prev, [chosenType]: (prev[chosenType] || []).slice(1) }));
    // Kick a refill after the pop so the slot starts rebuilding right away.
    setTimeout(() => tryFillSlot(), 0);
    return { ...chosenPuzzle, _servedType: chosenType, _servedIndex: idx };
  }, [enabledTypes, tryFillSlot]);

  const resetServedCount = useCallback(() => { servedCountRef.current = 0; }, []);

  return {
    queue,
    popNext,
    resetServedCount,
    // Diagnostics for the UI (queue depths, fetches in flight)
    sizes: {
      win: queue.win.length,
      passthrough: queue.passthrough.length,
      denial: queue.denial.length,
      threat: queue.threat.length,
    },
  };
}

export { DEFAULT_TARGET, FAST_TYPES, WARMUP_PUZZLES };
